// @flow
import * as React from 'react'
import { Transition } from 'react-transition-group'
import Popper from '../Popper'
import {
  DefaultOptionsList,
  DefaultOptionWrapper,
  OptionsLoadingWrapper,
  Container,
} from './elements'
import LazyListRenderer from './LazyListRenderer'
import NaiveListRenderer from './NaiveListRenderer'
import { eventToKey } from '../../utils'

// Types
export type RenderOptionFunc = (
  any,
  { focused?: boolean, index?: number }
) => any
export type RenderValueFunc = RenderOptionFunc
export type Props = {
  placement?: string,
  OptionWrapper?: any,
  OptionsList: any,
  wrapper?: any,
  maxWidth?: string,
  minWidth?: string,
  maxHeight?: string,
  popperModifiers?: Object,
  onKeyDown?: Function,
  onHidePopper?: Function,
  filterTargetProps: Function,
  onSelect?: (val: any, index: number) => any,
  onFocusOption?: (val: any, index: number) => any,
  target: (Function, Object, Object) => any,
  renderOption: RenderOptionFunc,
  renderContent?: Function,
  renderValue?: RenderValueFunc,
  optionSize?: number | ((index: number) => number) | Array<number>,
  options: Array<any>,
  loading?: boolean,
  showed?: boolean,
}
type State = {
  focusedIndex: number,
}

/**
 * Component for displaying dropdown lists. Keyboard navigatable.
 * @example
 * <Dropdown
 *   options={[1,2,3,4,5]}
 *   onSelect={action('select option')}
 *   onFocusOption={action('focus option')}
 *   target={(innerRef, targetProps) => (
 *     <Button {...targetProps} innerRef={innerRef}>
 *       Some Dropdown
 *     </Button>
 *   )}
 *   renderOption={option => (
 *     <div>{option}</div>
 *   )}
 * />
 * @param {Array} props.options           A list of options that you want to render
 * @param {Function} props.target         A function that should return a target element
 *                                        that will trigger the dropdown to show up.
 * @param {Function} props.renderOption   A function that should render provided option
 *                                        object. Should return a react element.
 * @param {Function} props.onSelect       A function that will be invoked when some option
 *                                        from the list will be selected
 * @param {Function} props.onFocusOption  A function that will be invoked when some of the
 *                                        options will be focused by the user.
 */
class Dropdown extends React.Component<Props, State> {
  state = { focusedIndex: -1 }

  optionsListNode = null

  popperInstance = null

  static defaultProps = {
    filterTargetProps: (targetProps: any) => ({
      onClick: targetProps.onClick,
      onKeyDown: targetProps.onKeyDown,
    }),
    renderOption: (x: any) => x,
    minWidth: '100px',
    maxWidth: '400px',
    placement: 'bottom',
    OptionWrapper: DefaultOptionWrapper,
    OptionsList: DefaultOptionsList,
    options: [],
  }

  componentDidUpdate(prevProps: Props) {
    if (this.popperInstance && prevProps.options !== this.props.options) {
      this.popperInstance.scheduleUpdate()
      this.setState({ focusedIndex: -1 })
    }
  }

  hidePopper() {
    if (this.popperInstance) {
      this.popperInstance.immidiateHidePopper()
    }
  }

  showPopper() {
    if (this.popperInstance) {
      this.popperInstance.showPopper()
    }
  }

  isPopperShowed() {
    if (this.popperInstance) {
      return this.popperInstance.isPopperShowed()
    }
    return false
  }

  selectFocusedOption() {
    const { onSelect, options } = this.props
    const indexToSelect =
      this.state.focusedIndex >= 0 ? this.state.focusedIndex : 0
    const option = options[indexToSelect]

    if (onSelect && option && !React.isValidElement(option)) {
      onSelect(option, indexToSelect)
      this.hidePopper()
    }
  }

  focusOptionIndex(index: number) {
    const { onFocusOption, options } = this.props
    const option = options[index]

    if (option && !React.isValidElement(option)) {
      this.setState({ focusedIndex: index })
      onFocusOption && onFocusOption(options[index], index)
    }
  }

  findIndexToFocus(direction: number): number {
    const { options } = this.props
    const currFocusedIndex = this.state.focusedIndex

    for (
      let i = currFocusedIndex + direction;
      i >= 0 && i < options.length;
      i += direction
    ) {
      const option = options[i]
      if (!React.isValidElement(option)) {
        return i
      }
    }

    return 0
  }

  handleKeyDown = (event: any) => {
    const { loading, onKeyDown } = this.props
    const key = eventToKey(event)
    const currFocusedIndex = this.state.focusedIndex
    let nextIndexToFocus = null
    let shouldSelect = false

    if (key === 'Esc') {
      this.hidePopper()
    } else if (key === 'ArrowUp') {
      event.preventDefault()
      nextIndexToFocus = this.findIndexToFocus(-1)
    } else if (key === 'ArrowDown') {
      event.preventDefault()
      nextIndexToFocus = this.findIndexToFocus(1)
    } else if (key === 'Enter' && this.isPopperShowed()) {
      nextIndexToFocus = currFocusedIndex
      shouldSelect = true
      event.stopPropagation()
      event.preventDefault()
    } else if (key === 'Tab' && this.isPopperShowed()) {
      nextIndexToFocus = currFocusedIndex
      shouldSelect = true
    }

    if (nextIndexToFocus !== null || key === 'Space') {
      this.showPopper()
    }

    if (!loading && nextIndexToFocus !== null) {
      if (this.optionsListNode) {
        this.focusOptionIndex(nextIndexToFocus)
        if (shouldSelect) {
          this.selectFocusedOption()
        }
      }
    }

    onKeyDown && onKeyDown(event)
  }

  handleOptionSelect = (val: any, index: number) => {
    const { onSelect } = this.props
    if (onSelect) onSelect(val, index)
    this.hidePopper()
  }

  handleHidePopper = () => {
    const { onHidePopper } = this.props
    this.setState({ focusedIndex: -1 })
    onHidePopper && onHidePopper()
  }

  setOptionsListRef = (node: ?Node) => {
    this.optionsListNode = node
  }

  setPopperRef = (node: any) => {
    this.popperInstance = node
  }

  renderTarget = (innerRef: Function, targetProps: Object, data: Object) =>
    this.props.target(
      innerRef,
      this.props.filterTargetProps({
        ...targetProps,
        onKeyDown: this.handleKeyDown,
      }),
      data
    )

  renderTransitionPopper = (
    innerRef: Function,
    popperProps: Object,
    data: Object
  ) => {
    const { roughPlacement, showed, offsets } = data
    const {
      maxHeight,
      minWidth,
      maxWidth,
      OptionWrapper,
      OptionsList,
      options,
      optionSize,
      renderOption,
      renderContent,
    } = this.props

    const ListComponent = optionSize ? LazyListRenderer : NaiveListRenderer

    const renderPopper = (state) => {
      const { loading } = this.props
      const actuallyShowed = state === 'entered'
      const listProps = {
        options,
        optionSize,
        renderOption,
        OptionWrapper,
        maxHeight,
        focusedIndex: this.state.focusedIndex,
        onOptionClick: this.handleOptionSelect,
      }

      return (
        <Container
          style={popperProps.style}
          onKeyDown={this.handleKeyDown}
          ref={innerRef}
          $showed={actuallyShowed}
          $placement={roughPlacement}
          $width={`${offsets.reference.width || 0}px`}
          $minWidth={minWidth}
          $maxWidth={maxWidth}
        >
          <OptionsList
            ref={this.setOptionsListRef}
            $loading={loading}
            $showed={actuallyShowed}
            $maxHeight={maxHeight}
          >
            <OptionsLoadingWrapper $loading={this.props.loading}>
              {!!renderContent && renderContent(listProps)}
              {!renderContent && <ListComponent {...listProps} />}
            </OptionsLoadingWrapper>
          </OptionsList>
        </Container>
      )
    }

    return (
      <Transition
        in={showed && (options.length > 0 || !!renderContent)}
        timeout={{ enter: 1, exit: 650 }}
        mountOnEnter
        unmountOnExit
      >
        {renderPopper}
      </Transition>
    )
  }

  render() {
    const { wrapper, placement, showed, popperModifiers } = this.props
    return (
      <Popper
        ref={this.setPopperRef}
        wrapper={wrapper}
        placement={placement}
        modifiers={popperModifiers}
        target={this.renderTarget}
        popper={this.renderTransitionPopper}
        onHidePopper={this.handleHidePopper}
        showed={showed}
      />
    )
  }
}

export default Dropdown
